import { useState, useEffect, useCallback } from 'react';

interface CustomFont {
  id: string;
  name: string;
  family: string;
  url: string;
  format: 'woff' | 'woff2' | 'ttf' | 'otf';
  size: number;
  uploadedAt: Date;
}

const STORAGE_KEY = 'dealercentives_custom_fonts';

export const useFontManager = () => {
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load custom fonts from localStorage on mount
  useEffect(() => {
    const loadStoredFonts = async () => {
      try {
        const storedFonts = localStorage.getItem(STORAGE_KEY);
        if (storedFonts) {
          const fonts: CustomFont[] = JSON.parse(storedFonts).map((font: any) => ({
            ...font,
            uploadedAt: new Date(font.uploadedAt)
          }));
          
          // Load fonts into document
          for (const font of fonts) {
            try {
              await loadFontIntoDocument(font);
            } catch (error) {
              console.warn(`Failed to load font ${font.name}:`, error);
            }
          }
          
          setCustomFonts(fonts);
        }
      } catch (error) {
        console.error('Error loading custom fonts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredFonts();
  }, []);

  // Save fonts to localStorage whenever customFonts changes
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(customFonts));
    }
  }, [customFonts, isLoading]);

  const loadFontIntoDocument = async (font: CustomFont): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if font is already loaded
      const existingFont = Array.from(document.fonts).find(f => f.family === font.family);
      if (existingFont) {
        resolve();
        return;
      }

      const fontFace = new FontFace(font.family, `url(${font.url})`);
      
      fontFace.load().then((loadedFont) => {
        document.fonts.add(loadedFont);
        resolve();
      }).catch((error) => {
        reject(error);
      });
    });
  };

  const addCustomFont = useCallback(async (font: CustomFont) => {
    try {
      // Load the font into the document
      await loadFontIntoDocument(font);
      
      // Add to state
      setCustomFonts(prev => [...prev, font]);
      
      return true;
    } catch (error) {
      console.error('Error adding custom font:', error);
      return false;
    }
  }, []);

  const removeCustomFont = useCallback((fontId: string) => {
    const font = customFonts.find(f => f.id === fontId);
    if (font) {
      // Remove from document fonts
      const fontFaces = Array.from(document.fonts);
      const fontFace = fontFaces.find(f => f.family === font.family);
      if (fontFace) {
        document.fonts.delete(fontFace);
      }
      
      // Remove from state
      setCustomFonts(prev => prev.filter(f => f.id !== fontId));
    }
  }, [customFonts]);

  const getAvailableFonts = useCallback(() => {
    const systemFonts = [
      'Arial, sans-serif',
      'Helvetica, sans-serif',
      'Georgia, serif',
      'Times New Roman, serif',
      'Verdana, sans-serif',
      'Trebuchet MS, sans-serif',
      'Impact, sans-serif',
      'Comic Sans MS, cursive',
      'Courier New, monospace',
      'Roboto, sans-serif',
      'Open Sans, sans-serif',
      'Lato, sans-serif',
      'Montserrat, sans-serif',
      'Source Sans Pro, sans-serif',
      'Raleway, sans-serif',
      'Poppins, sans-serif',
      'Nunito, sans-serif',
      'Playfair Display, serif',
      'Merriweather, serif',
      'Oswald, sans-serif'
    ];

    // Create proper font family strings for custom fonts
    const customFontFamilies = customFonts.map(font => font.family);
    
    return [...systemFonts, ...customFontFamilies];
  }, [customFonts]);

  const getFontDisplayName = useCallback((fontFamily: string) => {
    // Check if it's a custom font by looking for the custom- prefix
    if (fontFamily.startsWith('custom-')) {
      const customFont = customFonts.find(font => font.family === fontFamily);
      if (customFont) {
        return `${customFont.name} (Custom)`;
      }
      // Fallback for custom fonts that might not be found
      return `${fontFamily.replace('custom-', '').replace(/-/g, ' ')} (Custom)`;
    }
    
    // Return the first part of the font family for system fonts
    return fontFamily.split(',')[0];
  }, [customFonts]);

  const clearAllCustomFonts = useCallback(() => {
    // Remove all custom fonts from document
    customFonts.forEach(font => {
      const fontFaces = Array.from(document.fonts);
      const fontFace = fontFaces.find(f => f.family === font.family);
      if (fontFace) {
        document.fonts.delete(fontFace);
      }
    });
    
    // Clear state and storage
    setCustomFonts([]);
    localStorage.removeItem(STORAGE_KEY);
  }, [customFonts]);

  return {
    customFonts,
    isLoading,
    addCustomFont,
    removeCustomFont,
    getAvailableFonts,
    getFontDisplayName,
    clearAllCustomFonts
  };
};